import React, { useEffect, useState, createContext } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// --- Page & Layout Components ---
import DashboardPage from './component/pages/Dashboard/Dashboard';
import Sidebar from './component/pages/Dashboard/Sidebar';
import Navbar from './component/Navbar/Navbar';
import SettingPageMain from './component/pages/Dashboard/setting/pages/SettingPageMain';
import CommonMaster from './component/Common_master/CommonMaster.js';
import NotificationHandler from './component/NotificationHandler';

// --- HRMS Components ---
import Employees from './component/Employees/Employees';
import Onboarding from './component/onboarding/onboarding';
import Holidays from './component/Holidays/Holidays';
import Attendance from './component/Attendance/Attendance';
import LeavesManagement from './component/LeavesManagment/LeavesManagment';
import Payroll from './component/Payroll/Payroll';
import Offboarding from './component/Offboarding/Offboarding';

import './App.css';

// --- Create and export the RefreshContext ---
export const RefreshContext = createContext();

// --- REDIRECT URL ---
const ZTOOL_LOGIN_REDIRECT_URL = process.env.REACT_APP_ZTOOL_URL ? `${process.env.REACT_APP_ZTOOL_URL}/login` : '/login';

// --- JWT PARSING HELPER ---
const parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Auth Error: Failed to parse JWT.', error);
        return null;
    }
};

// --- HELPER COMPONENTS ---
const RedirectToLogin = ({ message }) => {
    useEffect(() => {
        // Only redirect if external URL is defined and we are not already on a login path
        if (ZTOOL_LOGIN_REDIRECT_URL && ZTOOL_LOGIN_REDIRECT_URL.startsWith('http')) {
            window.location.replace(ZTOOL_LOGIN_REDIRECT_URL);
        }
    }, []);
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <h1>{message || 'Redirecting to Login...'}</h1>
        </div>
    );
};
const ZTaskLoginPlaceholder = () => <RedirectToLogin />;

// --- PROTECTED ROUTE COMPONENT ---
const ProtectedRoute = ({ children, allowedRoles }) => {
    const stored = localStorage.getItem('zTaskUserDetails');
    let userRole = 'EMPLOYEE';
    if (stored) {
        try {
            const user = JSON.parse(stored);
            userRole = (user.role?.name || user.claims?.roles || 'EMPLOYEE').toUpperCase();
        } catch (e) { }
    }

    if (!allowedRoles.includes(userRole)) {
        return <DashboardPage />; // Or a custom 403 page
    }
    return children;
};

// --- Main Layout Component ---
const MainLayout = () => {
    const [refreshKey, setRefreshKey] = useState(0);
    const triggerRefresh = () => setRefreshKey((prevKey) => prevKey + 1);
    const handleGlobalUpdate = triggerRefresh;

    const mgmtRoles = ['SUPER_ADMIN', 'ADMIN', 'HR'];

    return (
        <RefreshContext.Provider value={{ triggerRefresh }}>
            <div className="app-layout">
                <Sidebar refreshKey={refreshKey} />
                <div className="main-content-area">
                    <Navbar />
                    <div className="page-content-wrapper">
                        <Routes>
                            <Route path="/" element={<DashboardPage />} />
                            <Route path="/dashboard" element={<DashboardPage />} />

                            {/* Organization Routes */}
                            <Route path="/organization/common-master" element={
                                <ProtectedRoute allowedRoles={mgmtRoles}>
                                    <CommonMaster />
                                </ProtectedRoute>
                            } />

                            {/* HRMS Routes */}
                            <Route path="/employees" element={
                                <ProtectedRoute allowedRoles={mgmtRoles}>
                                    <Employees />
                                </ProtectedRoute>
                            } />
                            <Route path="/onboarding" element={<Onboarding />} />
                            <Route path="/holidays" element={<Holidays />} />
                            <Route path="/attendance" element={<Attendance />} />
                            <Route path="/leaves" element={<LeavesManagement />} />
                            <Route path="/payroll" element={<Payroll />} />
                            <Route path="/offboarding-exit" element={<Offboarding />} />

                            {/* System & User Routes */}
                            <Route path="/settings/*" element={<SettingPageMain onProfileUpdate={handleGlobalUpdate} />} />
                            <Route path="/login" element={<ZTaskLoginPlaceholder />} />
                        </Routes>
                    </div>
                </div>
            </div>
        </RefreshContext.Provider>
    );
};


// --- AUTHENTICATION HANDLER ---
const AuthHandler = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [authStatus, setAuthStatus] = useState('PENDING');

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        let tokenFromUrl = searchParams.get('token');
        let userDetailsEncodedFromUrl = searchParams.get('userDetails');

        if (!tokenFromUrl && location.hash) {
            const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
            tokenFromUrl = hashParams.get('token');
            userDetailsEncodedFromUrl = hashParams.get('userDetails');
        }

        if (tokenFromUrl && userDetailsEncodedFromUrl) {
            localStorage.clear();
            try {
                const userDetails = JSON.parse(decodeURIComponent(userDetailsEncodedFromUrl));
                localStorage.setItem('zTaskAuthToken', tokenFromUrl);
                localStorage.setItem('zTaskUserDetails', JSON.stringify(userDetails));
                navigate(location.pathname, { replace: true, search: '' });
                setAuthStatus('AUTHENTICATED');
            } catch (e) {
                console.error('Auth Error: Failed to parse user details from URL.', e);
                localStorage.clear();
                setAuthStatus('FAILED');
            }
            return;
        }

        const tokenFromLocalStorage = localStorage.getItem('zTaskAuthToken');
        if (tokenFromLocalStorage) {
            const decodedToken = parseJwt(tokenFromLocalStorage);
            if (decodedToken && decodedToken.exp * 1000 < Date.now()) {
                localStorage.clear();
                setAuthStatus('EXPIRED');
            } else {
                setAuthStatus('AUTHENTICATED');
            }
        } else {
            setAuthStatus('FAILED');
        }
    }, [location, navigate]);

    if (authStatus === 'PENDING') {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><h1>Authenticating...</h1></div>;
    }
    if (authStatus === 'EXPIRED') {
        return <RedirectToLogin message="Your session has expired. Redirecting to login..." />;
    }
    if (authStatus === 'FAILED') {
        return <RedirectToLogin />;
    }
    return (
        <>
            <NotificationHandler />
            {children}
        </>
    );
};



// --- Main App Component ---
const App = () => {
    return (
        <BrowserRouter>
            <AuthHandler>
                <MainLayout />
            </AuthHandler>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
        </BrowserRouter >
    );
};

export default App;